'use client'
import Link from 'next/link'
import { useRef } from 'react'
import { AnimatedNumber } from '@/components/ui/animated-number'
import {
  ArrowUpRight,
  Calculator,
  DataBase as Database,
  Meter as Gauge,
  Education as GraduationCap,
  BareMetalServer as Server,
  BareMetalServer_02 as ServerCog,
  SecurityServices as ShieldCheck,
  Group as Users,
  Wallet,
} from '@carbon/icons-react'

import { cn } from '@/lib/utils'
import { RibbonCanvas } from '@/components/fx/ribbon-canvas'

/**
 * Architecture bento — blume-bento card structures mixed with the leaf
 * sample's look: flowing ribbon fields inside cards, floating tiles with
 * soft leaf shadows, and cursor badges. Four cards cover the eight
 * architecture pillars: monitoring+performance (graph), ERP systems
 * (ribbon + module tiles), infrastructure+scalability (concentric rings
 * over a ribbon), security+privacy (editor with collaborator badges).
 */

const accentAlpha = (a: number) => `hsl(var(--accent-green) / ${a})`

/* ─── Shared card chrome ─────────────────────────────────────────────── */

function BentoCard({
  className,
  children,
  nav = true,
}: {
  className?: string
  children: React.ReactNode
  /** Render the corner hover-arrow link to /architecture */
  nav?: boolean
}) {
  return (
    <div
      className={cn(
        'group relative flex flex-col gap-4 overflow-hidden rounded-3xl',
        'border border-border/70 bg-background p-5',
        'transition-all duration-300',
        className
      )}
    >
      {/* Brand wash on its own layer: as a sibling class, tailwind-merge
          drops the bg-muted shade in favour of the gradient */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[hsl(var(--accent-green)/0.10)]"
      />
      {nav && (
        <Link
          href="/architecture"
          aria-label="Learn about the architecture"
          className="absolute right-4 top-4 z-20 rounded-full bg-accent/50 p-3 opacity-0 transition-opacity group-hover:opacity-100"
        >
          <ArrowUpRight className="size-4" />
        </Link>
      )}
      {children}
    </div>
  )
}

function CardCopy({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="relative z-10 flex flex-col gap-3">
      <Icon className="size-7 text-foreground/70" />
      <h3 className="text-xl font-semibold leading-none tracking-tight text-foreground sm:text-2xl">
        {title}
      </h3>
      <div className="flex flex-col gap-2 text-balance text-sm text-foreground/75 sm:text-base">
        {children}
      </div>
    </div>
  )
}

/** Soft radial accent wash behind a card's visual. Deliberately subtle. */
function AccentGlow({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        'pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[85%] -translate-x-1/2 -translate-y-1/2',
        'opacity-40',
        className
      )}
      style={{
        background: `radial-gradient(50% 50% at 50% 50%, ${accentAlpha(0.12)}, transparent 70%)`,
      }}
    />
  )
}

/* ─── Card A: Monitoring & Performance (blume Bento9v1 graph) ────────── */

function MetricsGraph() {
  const areaRef = useRef<HTMLDivElement | null>(null)
  const bubbleRef = useRef<HTMLDivElement | null>(null)

  const onMouseMove = (e: React.MouseEvent) => {
    const area = areaRef.current
    const bubble = bubbleRef.current
    if (!area || !bubble) return
    const r = area.getBoundingClientRect()
    const dx = (e.clientX - (r.left + r.width / 2)) / r.width
    const dy = (e.clientY - (r.top + r.height / 2)) / r.height
    bubble.style.transform = `translate(${dx * 16}px, ${dy * 12}px)`
  }
  const onMouseLeave = () => {
    if (bubbleRef.current)
      bubbleRef.current.style.transform = 'translate(0px, 0px)'
  }

  return (
    <div
      ref={areaRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      className="relative -mx-24 -mb-5 min-h-[230px] grow select-none"
    >
      {/* Anchored to the card's bottom edge so the area fill runs off the
          card instead of ending in a visible horizontal cut. */}
      <div className="absolute bottom-0 left-1/2 h-[230px] w-[910px] -translate-x-1/2">
        {/* Horizontal grid lines, self-fading at both ends */}
        <div className="absolute inset-x-0 top-2 z-[1] grid h-[210px] grid-rows-6 text-emerald-600/25 dark:text-primary-active/25">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-px w-full"
              style={{
                background:
                  'linear-gradient(to right, transparent 0%, currentColor 15%, currentColor 85%, transparent 100%)',
              }}
            />
          ))}
        </div>

        {/* Graph SVG, edge-faded */}
        <div
          className="absolute inset-0 z-[2]"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)',
          }}
        >
          <svg
            width="910"
            height="230"
            viewBox="0 0 910 317"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="h-full w-full"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="arch-bento-area" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0"
                  stopColor="hsl(var(--foreground))"
                  stopOpacity="0.08"
                />
                <stop
                  offset="1"
                  stopColor="hsl(var(--foreground))"
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>
            <path
              d="M1.12348 256L44.9383 203.912L90.4383 209.408L135.938 216.735L157.284 198.5L226.938 170.941L272.438 161.782L317.938 176.436L363.438 143.464L406.691 155L455 110L498.253 128L545.438 130.726L590.826 106.967L636.438 169.109L681.938 147.127L727.438 172.772L773.5 122L818.438 164L868.994 45.5L909.438 125.146V315.65H-0.561707L1.12348 256Z"
              fill="url(#arch-bento-area)"
            />
            <path
              d="M-2.24689 262.5L44.9383 203.912L90.4383 209.408L135.938 216.735L157.284 197.5L226.938 170.941L272.438 161.782L317.938 176.436L363.438 143.464L406.691 156L455 110L498.253 128L545.438 130.103L590.938 105.82L636.438 169.109L681.938 147.127L727.438 172.772L773.5 122L819 164.5L868.994 44L910 126.5"
              stroke="hsl(var(--foreground) / 0.35)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              className="stroke-emerald-600 dark:stroke-primary-active"
              d="M-2.24689 249.5L46.0618 170L90.4383 180L135.938 157L181.438 155.5L226.938 131.5L272.438 112L317.938 127L363.438 94L408.938 91L455 62L498.815 90L545.438 101L590.938 77.5L642.056 76L686.432 45L723.506 88.5L781.926 29L817.877 56L876.296 2L917.864 103.5"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Live metrics bubble — trails the cursor over the graph area */}
        <div
          ref={bubbleRef}
          className="absolute left-1/2 top-6 z-10 ml-6 w-60 transition-transform duration-300 ease-out will-change-transform"
        >
          <div className="rounded-2xl bg-background px-4 py-3 text-sm shadow-[0_12px_32px_rgba(8,9,10,0.10)] ring-1 ring-border/50">
            <div className="mb-2 font-medium text-foreground">Live metrics</div>
            <div className="flex items-center gap-3 py-1">
              <span className="h-3.5 min-w-3.5 rounded-[4px] bg-emerald-600 dark:bg-primary-active" />
              <span className="whitespace-nowrap text-muted-foreground">
                Latency p99
              </span>
              <span className="w-full whitespace-nowrap text-end text-foreground">
                <AnimatedNumber value="42 ms" />
              </span>
            </div>
            <div className="flex items-center gap-3 py-1">
              <span className="h-3.5 min-w-3.5 rounded-[4px] bg-foreground/70" />
              <span className="whitespace-nowrap text-muted-foreground">
                Throughput
              </span>
              <span className="w-full whitespace-nowrap text-end text-foreground">
                <AnimatedNumber value="12.4k rps" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Card B: ERP Systems (leaf ConnectCard: ribbon + floating tiles) ── */

const ERP_MODULES = [
  { icon: Calculator, label: 'Accounting' },
  { icon: Wallet, label: 'Payroll' },
  { icon: ServerCog, label: 'ERP core', accent: true },
  { icon: Users, label: 'HRMS' },
  { icon: GraduationCap, label: 'LMS' },
]

function ErpModuleTiles() {
  return (
    <div className="pointer-events-none relative min-h-[210px] grow select-none">
      <RibbonCanvas
        settings={{
          rotate: 15,
          edgeFade: 0.12,
          background: 'transparent',
          opacity: 0.55,
          speed: 0.8,
        }}
        maxDpr={1.5}
        className="absolute -right-[12%] top-[4%] h-[150%] w-[150%]"
      />
      <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-center gap-3">
        {ERP_MODULES.map(({ icon: Icon, label, accent }, i) => (
          <span
            key={label}
            title={label}
            // Staggered delay turns the uniform nudge into a wave that
            // ripples across the row (and back on hover-out)
            style={{ transitionDelay: `${i * 50}ms` }}
            className={cn(
              'flex size-14 items-center justify-center rounded-2xl bg-background',
              'shadow-[0_10px_28px_rgba(8,9,10,0.08)] transition-all duration-500 ease-out',
              'group-hover:-translate-y-2 group-hover:shadow-[0_16px_36px_rgba(8,9,10,0.14)]',
              accent
                ? 'ring-2 ring-emerald-600/50 dark:ring-primary-active/60 group-hover:scale-105 group-hover:ring-emerald-600 dark:group-hover:ring-primary-active'
                : 'ring-1 ring-border/50'
            )}
          >
            <Icon
              className={cn(
                'size-7',
                accent
                  ? 'text-emerald-600 dark:text-primary-active'
                  : 'text-foreground/80'
              )}
            />
          </span>
        ))}
      </div>
      {/* Chip floating below the tile row, leaf-pill style */}
      <div className="absolute inset-x-0 top-[74%] flex justify-center">
        <span className="flex items-center gap-2 rounded-full bg-background/70 backdrop-blur-md py-2 pl-3 pr-4 text-xs font-medium text-muted-foreground shadow-[0_12px_32px_rgba(8,9,10,0.10)]">
          <Database className="size-4 text-emerald-600 dark:text-primary-active" />
          Five modules · one shared data model
        </span>
      </div>
    </div>
  )
}

/* ─── Card C: Infrastructure & Scalability (blume Bento10v1 rings) ───── */

function ConcentricRings() {
  const rings = Array.from({ length: 10 }, (_, i) => (10 - i) * 0.055)

  const center = (
    <div className="flex h-full w-full items-center justify-center rounded-full">
      <div
        className="flex size-16 items-center justify-center rounded-full border bg-background/80 shadow-md backdrop-blur-md"
        style={{ borderColor: accentAlpha(0.3) }}
      >
        <Server className="size-7 text-foreground" />
      </div>
    </div>
  )

  return (
    <div className="pointer-events-none relative z-10 flex grow select-none items-center justify-center py-2">
      <div className="relative h-[240px] w-[240px]">
        {rings.reduceRight(
          (content, alpha) => (
            <div
              className="relative flex h-full w-full items-center justify-center rounded-full border p-2 transition-transform duration-1000 ease-in-out group-hover:scale-[1.02]"
              style={{ borderColor: accentAlpha(alpha) }}
            >
              {content}
            </div>
          ),
          center
        )}
      </div>
    </div>
  )
}

/* ─── Card D: Security & Privacy (blume Bento6v1 editor) ─────────────── */

const AUTH_CODE = [
  '// Security middleware',
  'const auth = async (req, res, next) => {',
  '  const token = await verifyJWT(req.headers.authorization);',
  '  req.user = await validatePermissions(token);',
  '  next();',
  '}',
]

const PRIVACY_CODE = [
  '// Privacy controls',
  'const maskSensitiveData = (data) => {',
  '  return {',
  '    ...data,',
  '    ssn: mask(data.ssn),',
  '    email: anonymize(data.email)',
  '  }',
  '}',
]

function CodePane({ lines }: { lines: string[] }) {
  return (
    <pre className="font-mono text-xs leading-5">
      {lines.map((line, i) => (
        <span
          key={i}
          className={cn(
            'block whitespace-pre',
            line.trim().startsWith('//')
              ? 'text-emerald-600 dark:text-primary-active'
              : 'text-muted-foreground'
          )}
        >
          {line || ' '}
        </span>
      ))}
    </pre>
  )
}

function SecurityEditor() {
  return (
    <div className="flex grow items-end justify-center">
      <div className="min-h-[220px] w-full grow items-center self-center px-0 sm:px-4">
        <div className="relative h-full w-full">
          <div className="relative z-10 mx-auto w-full max-w-[460px]">
            <div className="relative flex min-h-[220px] flex-col gap-4 rounded-xl border border-border/70 bg-background/50">
              {/* Editor top bar */}
              <div className="flex w-full items-center justify-start gap-4 overflow-hidden py-2">
                <div className="hidden gap-2 pl-4 sm:flex">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="size-3 rounded-full bg-foreground/10"
                    />
                  ))}
                </div>
                <div className="relative flex w-[280px]">
                  <div className="relative z-10 flex grow basis-0 items-center gap-2 px-4 py-1.5 text-xs text-muted-foreground">
                    <ShieldCheck className="size-4 text-emerald-600 dark:text-primary-active" />
                    <p>auth.ts</p>
                  </div>
                  <div className="relative z-10 flex grow basis-0 items-center gap-2 px-4 py-1.5 text-xs text-muted-foreground">
                    <p>privacy.ts</p>
                  </div>
                  {/* Sliding active-tab indicator */}
                  <div className="absolute left-0 top-0 h-full w-[50%] px-2 transition-all duration-1000 ease-in-out md:group-hover:left-[50%]">
                    <div className="h-full w-full rounded-md bg-background/80 shadow-md ring-1 ring-border/50" />
                  </div>
                </div>
              </div>

              {/* Crossfading panes */}
              <div className="relative w-full grow overflow-hidden pb-4">
                <div className="px-6 transition-all duration-1000 ease-in-out md:group-hover:-translate-x-8 md:group-hover:opacity-0">
                  <CodePane lines={AUTH_CODE} />
                </div>
                <div className="absolute left-0 top-0 w-full translate-x-8 px-6 opacity-0 transition-all duration-1000 ease-in-out md:group-hover:translate-x-0 md:group-hover:opacity-100">
                  <CodePane lines={PRIVACY_CODE} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── The bento grid ─────────────────────────────────────────────────── */

export function ArchitectureBento() {
  return (
    <div className="grid grid-cols-12 gap-4">
      <BentoCard className="col-span-12 sm:col-span-6 lg:col-span-7">
        <CardCopy icon={Gauge} title="Real-time monitoring & performance">
          <p className="max-w-[460px]">
            Distributed tracing, performance metrics, and error tracking with
            advanced caching and query optimization at every layer.
          </p>
        </CardCopy>
        <AccentGlow />
        <MetricsGraph />
      </BentoCard>

      <BentoCard className="col-span-12 sm:col-span-6 lg:col-span-5">
        <CardCopy icon={ServerCog} title="Unified ERP architecture">
          <p className="max-w-[460px]">
            Accounting, HR, payroll, and learning on shared data models with
            unified authentication and cross-module workflows.
          </p>
        </CardCopy>
        <ErpModuleTiles />
      </BentoCard>

      <BentoCard className="col-span-12 sm:col-span-6 lg:col-span-5">
        <AccentGlow />
        <ConcentricRings />
        <CardCopy icon={Server} title="Cloud-native, built to scale">
          <p className="max-w-[460px]">
            Microservices, containerization, and auto-scaling with load
            balancing: millions of transactions, zero-downtime deployments.
          </p>
        </CardCopy>
      </BentoCard>

      <BentoCard
        nav={false}
        className="col-span-12 sm:col-span-6 lg:col-span-7"
      >
        <CardCopy
          icon={ShieldCheck}
          title="Enterprise-grade security & privacy"
        >
          <p className="max-w-[460px]">
            End-to-end encryption, role-based access, automated PII masking, and
            audit trails.
          </p>
        </CardCopy>
        {/* Certification marks pinned to the card's top-right corner */}
        <div className="absolute right-5 top-5 z-20 flex items-center gap-4 sm:right-6 sm:top-6">
          <img
            src="/certifications/iso-9001-2015.svg"
            alt="ISO 9001:2015 certified"
            width={595}
            height={544}
            className="h-8 w-auto dark:brightness-0 dark:invert sm:h-10 lg:h-14"
            loading="lazy"
            decoding="async"
          />
          <img
            src="/certifications/iso-27001-2022.svg"
            alt="ISO 27001:2022 certified"
            width={595}
            height={544}
            className="h-8 w-auto dark:brightness-0 dark:invert sm:h-10 lg:h-14"
            loading="lazy"
            decoding="async"
          />
        </div>
        <AccentGlow />
        <SecurityEditor />
      </BentoCard>
    </div>
  )
}
